var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { camelCase } from 'lodash';
import { createContext, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlatformContext } from './selectors';
/**
 * Integration terminology scope.
 *
 * Labels across the app adapt to the platform a thing came from via i18next context suffixes
 * (e.g. `queueName` vs `queueName_genesys`). Historically the context came only from the org type
 * (embedded Genesys org → 'genesys'), but terminology can be scoped more locally:
 * - plan wizard / report: the plan's importSource (a standalone plan imported from Genesys shows
 *   "Planning Groups")
 * - org config Clients step: the integration selected on the client being configured
 *
 * Resolution happens at provide time (scoped value ?? nearest provider ?? platform/org type), so
 * consumers are a bare useContext and don't each subscribe to the user session store. A valueless
 * provider at the app root resolves to the platform context, keeping unscoped areas on the old
 * org-type behavior.
 */
var TerminologyContext = createContext(undefined);
export var TerminologyProvider = function (_a) {
    var _b, _c;
    var value = _a.value, children = _a.children;
    var inherited = useContext(TerminologyContext);
    var platform = usePlatformContext();
    var resolved = (_c = (_b = (value ? camelCase(value) : undefined)) !== null && _b !== void 0 ? _b : inherited) !== null && _c !== void 0 ? _c : platform;
    return _jsx(TerminologyContext.Provider, __assign({ value: resolved }, { children: children }));
};
export var useTerminology = function () { return useContext(TerminologyContext); };
/**
 * useTranslation with the terminology context baked into `t`, so every label in the component —
 * including ones added later — follows the scoped terminology without threading `{ context }`
 * by hand. Keys without a context variant fall back to the base key as usual.
 */
export var useTerminologyTranslation = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var ret = useTranslation.apply(void 0, args);
    var context = useTerminology();
    var baseT = ret.t;
    var t = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (key, options) { return baseT(key, __assign({ context: context }, options)); }, [baseT, context]);
    // Mirror react-i18next's hybrid array/object response shape
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var result = [t, ret.i18n, ret.ready];
    result.t = t;
    result.i18n = ret.i18n;
    result.ready = ret.ready;
    return result;
};
